import React, { useEffect } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useRef } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import axios from 'helpers/api';
import { toast } from 'react-toastify';
import Spinner from 'react-bootstrap/Spinner';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';
import * as accessControlObj from '../authentication/accessControlMap'
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

const isGivenInDayProgram = value => value === 1 || value === '1' || value === true;

const DayProgramToggle = ({ allSelected, onToggle, dayProgramCount }) => (
    <div
        className="d-flex justify-content-end align-items-center"
        style={{ height: 'calc(1.5em + 0.75rem + 2px)' }}
    >
        <Form.Check
            type="switch"
            className="mb-0"
            name="is_given_in_dayprogram"
            id="is_given_in_dayprogram"
            checked={allSelected}
            disabled={dayProgramCount === 0}
            onChange={onToggle}
            label="Select Meds Given in Day Program"
        />
    </div>
);

DayProgramToggle.propTypes = {
    allSelected: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    dayProgramCount: PropTypes.number.isRequired
};

const isExpiredPrescription = stopDate => {
    if (!stopDate) return false;

    const parsedStopDate = moment(stopDate);
    return parsedStopDate.isValid() && parsedStopDate.isBefore(moment(), 'day');
};

const MedSheetForm = (props) => {
    const originalPageTitleRef = useRef(document.title);
    const [reportFormat, setReportFormat] = useState(localStorage.getItem('medsheetFormat') || '0');
    const [medsheet, setMedSheet] = useState("");
    const [checked, setChecked] = React.useState(true);
    const [spinner, showSpinner] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [unverifiedCount, setUnverifiedCount] = useState(0);
    const [reportDateError, setReportDateError] = useState(false);
    const [reportFormatError, setReportFormatError] = useState(false);
    const [formData, setFormData] = useState({
        reportdate: '',
        reportformat: '',
        is_drug_hcp: 'drug',
        is_prn: true,
        is_regular: true,
        selected_drug_only: false,
        selected_medgroup_only: false,
        select_all: true
    });

    const [reportdate, setReportDate] = useState(null);
    const [showResults, setShowResults] = React.useState(false)
    const [tableData, setTableData] = useState([]);
    const [medGroupData, setMedGroupTableData] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedMedGroupIds, setSelectedMedGroupIds] = useState([]);
    const [iframeSrc, setIframeSrc] = useState(null);
    const { programId } = useParams();
    const active = props?.data?.prescriptions?.filter(p => (p.discontinued_date == null && p.is_verified == 1)) || [];

    const columns = [
        {
            id: 'select',
            accessorKey: '',
            header: 'Select',
            cell: ({ row }) => {
                const id = row.original.id;
                const isExpired = row.original.isExpired;
                return (
                    <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(id)}
                        disabled={isExpired}
                        title={isExpired ? 'Expired prescriptions are not included in the medsheet' : undefined}
                        onChange={() => {
                            if (isExpired) return;

                            setSelectedIds(prev =>
                                prev.includes(id)
                                    ? prev.filter(pid => pid !== id)
                                    : [...prev, id]
                            );
                        }}
                    />
                );
            },
            meta: {
                headerProps: { className: 'text-900' },
            }
        },
        {
            id: 'drug',
            header: "Drug",
            accessorKey: "drug",
            cell: ({ row }) => {
                const { drug, isExpired, stop } = row.original;

                return (
                    <div className={isExpired ? 'text-danger fw-semibold' : ''}>
                        <span>{drug}</span>
                        {isExpired && (
                            <span className="badge bg-danger-subtle text-danger ms-2">
                                <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                                Expired{stop ? ` ${stop}` : ''}
                            </span>
                        )}
                    </div>
                );
            },
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            }
        },
        // {
        //     id: 'prn',
        //     header: "PRN",
        //     accessorKey: "prn",
        //     meta: {
        //         headerProps: { className: 'text-900 ' },
        //     },
        //     cell: ({ row }) => {
        //         const { med_type } = row.original;
        //         return (med_type == 'P') ? <FontAwesomeIcon icon="check" className="ms-1 text-success " /> : <FontAwesomeIcon className="text-danger" icon="times-circle" />;
        //     },
        // },
        {
            id: 'medsheet',
            header: "Medsheet",
            accessorKey: "medsheet",
            cell: ({ row }) => {
                const { mar_flag, isExpired } = row.original;
                return (mar_flag === 1 && !isExpired) ? <FontAwesomeIcon icon="check" className="ms-1 text-success " /> : <FontAwesomeIcon className="text-danger" icon="times-circle" />;
            },
            meta: {
                headerProps: { className: 'text-900 ' },
            }
        },
        {
            id: 'given_in_dayprogram',
            header: "Given in Day Program",
            accessorKey: "is_given_in_dayprogram",
            cell: ({ row }) => {
                return isGivenInDayProgram(row.original.is_given_in_dayprogram)
                    ? <FontAwesomeIcon icon="check" className="ms-1 text-success" />
                    : null;
            },
            meta: {
                headerProps: { className: 'text-900 ' },
            }
        },
        // {
        //     id: 'start',
        //     header: "Start",
        //     accessorKey: "start",
        //     meta: {
        //         headerProps: { className: 'text-900 text-start' },
        //         cellProps: { className: 'text-start' }
        //     }
        // }
    ];

    const columns2 = [
        {
            id: 'select2',
            accessorKey: '',
            header: 'Select',
            cell: ({ row }) => {
                const id = row.original.id;
                return (
                    <Form.Check
                        type="checkbox"
                        checked={selectedMedGroupIds.includes(id)}
                        onChange={() => {
                            setSelectedMedGroupIds(prev =>
                                prev.includes(id)
                                    ? prev.filter(pid => pid !== id)
                                    : [...prev, id]
                            );
                        }}
                    />
                );
            },
            meta: {
                headerProps: { className: 'text-900' },
            }
        },
        {
            id: 'medgroup',
            header: "Med Group",
            accessorKey: "medgroup",
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            }
        },
    ]

    const navigate = useNavigate();
    let localStorageAccessControl = localStorage.getItem("accessControl");

    const openInNewTab = (url) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
    }

    useEffect(() => {
        if (spinner) {
            setShowResults(false);
        }
    }, [spinner])

    useEffect(() => {
        fetchTable()
        fetchMedGroup()

        return () => {
            document.title = originalPageTitleRef.current;
        };
    }, [])

    // const fetchMedGroup = async () => {
    //     let tokenStr = localStorage.getItem("accessToken");
    //     let pelmedsMedSheetUrl = `${process.env.REACT_APP_SERVER_URL}` + "/prescriptions/fetch-multiple";
    //     const { data } = await axios.post(pelmedsMedSheetUrl, { medgroup: {} }, { headers: { 'Authorization': `Bearer ${tokenStr}` } });

    //     if (data?.output?.status) {
    //         const medGroupData = data?.output?.data["medgroup"]?.map((item) => ({
    //             id: item.id,
    //             medgroup: item.name,
    //         }));
    //         setMedGroupTableData(medGroupData);
    //     } else {
    //         // toast.error(data.message);
    //     }
    // }


    const fetchMedGroup = async () => {
        let tokenStr = localStorage.getItem("accessToken");
        let pelmedsMedSheetUrl = `${process.env.REACT_APP_SERVER_URL}` + "/prescriptions/fetch-multiple";

        const { data } = await axios.post(
            pelmedsMedSheetUrl,
            { medgroup: {} },
            { headers: { 'Authorization': `Bearer ${tokenStr}` } }
        );

        if (data?.output?.status) {
            const responseMedGroups = data?.output?.data["medgroup"] || [];

            const validMedGroupIds = new Set(
                [
                    ...props?.data?.prescriptions
                        ?.filter(p => p.discontinued_date == null && p.is_verified == 1)
                        .map(p => p.medgroup_id)
                        .filter(id => id != null),
                    ...props?.data?.medical_records
                        // ?.filter(p => p.discontinued_date == null && p.is_verified == 1)
                        .map(p => p.med_group_id)
                        .filter(id => id != null)
                ]
            );

            const filteredMedGroupData = responseMedGroups
                .filter(group => validMedGroupIds.has(group.id))
                .map(item => ({
                    id: item.id,
                    medgroup: item.name
                }));

            setMedGroupTableData(filteredMedGroupData);
        }
    };

    const fetchTable = () => {
        const data = active
            .map((item) => ({
                id: item.id,
                select: '',
                drug: `${item.drug} ${(item.strength_value ?? "")} ${(item.strength ?? "")}`.trim(),
                prn: item.is_prn,
                medsheet: item.is_medsheet,
                start: item.start_date != null ? moment(item.start_date).format("MM/DD/YY") : '',
                stop: item.client_stopdate != null ? moment(item.client_stopdate).format("MM/DD/YY") : '',
                isExpired: isExpiredPrescription(item.client_stopdate),
                mar_flag: item.mar_flag,
                med_type: item.med_type,
                is_given_in_dayprogram: item.is_given_in_dayprogram,
                medgroup_id: item.medgroup_id != null ? item.medgroup_id : ''
            }))
            .sort((a, b) => a.drug.localeCompare(b.drug));
        setTableData(data);
    };

    const getSelectedMedsheetPrescriptionIds = () =>
        selectedIds.filter(id => {
            const prescription = tableData.find(item => item.id === id);
            return !prescription?.isExpired;
        });

    const dayProgramIds = tableData
        .filter(item => isGivenInDayProgram(item.is_given_in_dayprogram) && !item.isExpired)
        .map(item => item.id);

    const allDayProgramSelected =
        dayProgramIds.length > 0 && dayProgramIds.every(id => selectedIds.includes(id));

    const toggleDayProgramMeds = () => {
        setSelectedIds(prev =>
            allDayProgramSelected
                ? prev.filter(id => !dayProgramIds.includes(id))
                : Array.from(new Set([...prev, ...dayProgramIds]))
        );
    };


    const table2 = useAdvanceTable({
        data: medGroupData,
        columns: columns2,
        selection: false,
        sortable: true,
        pagination: true
    });

    const table = useAdvanceTable({
        data: tableData,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const generateTreatmentReport = async () => {
        if ((localStorage.getItem("patientId") != null || localStorage.getItem("reportFacilityId") != null) && reportFormat != "" && reportdate != null) {

            showSpinner(true);

            let tokenStr = localStorage.getItem("accessToken");
            let pelmedsMedSheetUrl = `${process.env.REACT_APP_SERVER_URL}` + "/reports/medsheet-exp";
            const selectedMedsheetPrescriptionIds = getSelectedMedsheetPrescriptionIds();

            //console.log("is_drug_hcp " + localStorage.getItem("patientId"));

            let pelmedsMedSheetData = props.type == 'patient' ?
                {
                    date: reportdate != null ? moment(reportdate).format('MMM YYYY') : null,//"01-10-2024",            
                    patient_id: localStorage.getItem("patientId"),
                    medsheet_format: reportFormat,
                    ...(formData.selected_drug_only && { selected_prescriptions: selectedMedsheetPrescriptionIds }),
                    ...(formData.selected_medgroup_only && { selected_med_groups: selectedMedGroupIds }),
                    is_hcp: formData.is_drug_hcp == "hcp" ? 1 : 0,
                    is_drug: formData.is_drug_hcp == "drug" ? 1 : 0,
                    // is_medgroup: formData.is_medgroup ? 1 : 0,
                    // is_highalert: formData.is_highalert ? 1 : 0,
                    is_prn: formData.is_prn ? 1 : 0,
                    is_regular: formData.is_regular ? 1 : 0
                } :
                {
                    date: reportdate != null ? moment(reportdate).format('MMM YYYY') : null,//"01-10-2024",            
                    facility_id: localStorage.getItem("reportFacilityId"),
                    medsheet_format: reportFormat,
                    is_hcp: formData.is_drug_hcp == "hcp" ? 1 : 0,
                    is_drug: formData.is_drug_hcp == "drug" ? 1 : 0,
                    //   is_medgroup: formData.is_medgroup ? 1 : 0,
                    //   is_highalert: formData.is_highalert ? 1 : 0,
                    is_prn: formData.is_prn ? 1 : 0,
                    is_regular: formData.is_regular ? 1 : 0
                };

            if (programId) {
                pelmedsMedSheetData.day_program_id = programId;
            }

            //console.log("pelmedsMedSheetData " + JSON.stringify(pelmedsMedSheetData));

            const data = await axios.post(pelmedsMedSheetUrl, pelmedsMedSheetData, { headers: { 'Authorization': `Bearer ${tokenStr}` } });

            if (data?.data?.status == false) {
                toast.error(data.data.message);
            }
            else {
                toast.success("MedSheet Generated Successfully");
                const { blobUrl } = await createIframeReportBlobUrl(data.data);
                setIframeSrc(blobUrl);
            }
            showSpinner(false);
        }
    }

    // Handler
    const handleSubmit = async e => {
        e.preventDefault();
        if (props?.validateFacility) {
            if (!props?.validateFacility()) return;
        }
        const dateValid = reportdate !== null;
        const formatValid = reportFormat != '0';
        const selectedMedsheetPrescriptionIds = getSelectedMedsheetPrescriptionIds();
        const selectionValid =
            (formData.selected_drug_only && selectedMedsheetPrescriptionIds.length > 0) ||
            (formData.selected_medgroup_only && selectedMedGroupIds.length > 0);

        setReportDateError(!dateValid);
        setReportFormatError(!formatValid);

        if (!dateValid || !formatValid) {
            return;
        }
        if (!selectionValid && !formData.select_all) {
            toast.error("Please select at least one row from the selected table.");
            return;
        }
        const unverified = props?.data?.prescriptions?.filter(p => (p.is_verified == 0 && p.discontinued_date == null)) || [];
        const isSharedLiving = Boolean(
            props?.data?.facility_is_sharedliving_facility === true ||
            props?.data?.facility_is_sharedliving_facility === 1 ||
            props?.data?.facility_is_sharedliving_facility === '1' ||
            props?.data?.facility_is_sharedliving_facility === 'true'
        );

        if (!isSharedLiving && unverified.length > 0 && localStorageAccessControl.includes(accessControlObj.ACCESS_CONTROL["mym"])) {
            setUnverifiedCount(unverified.length);
            setShowConfirmModal(true);
            return;
        }
        generateTreatmentReport();
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;

        if (name === "reportformat") {
            setReportFormat(value);
        }
        setShowResults(false);
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };


    const handleRadioChange = e => {
        setShowResults(false);
        setChecked((state) => !state)
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }

    const handleUnverifiedView = () => {
        setShowConfirmModal(false);
        props.goToTab('Prescription', {
            prefill: { selected: 'unverified' },
            source: 'prescription',
            focus: 'contact-section'
        });
    }

    return (
        <div>
            <Form onSubmit={handleSubmit}>
                {showConfirmModal && (
                    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
                        <div className="modal-dialog" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Unverified Orders</h5>
                                </div>
                                <div className="modal-body">
                                    <p>There are {unverifiedCount} unverified orders, do you want to continue?</p>
                                </div>
                                <div className="modal-footer d-flex justify-content-between">
                                    <Button variant="secondary" onClick={() => handleUnverifiedView()}>
                                        Unverified Orders
                                    </Button>
                                    <div>
                                        <Button variant="primary" className="me-2" onClick={() => {
                                            setShowConfirmModal(false);
                                            generateTreatmentReport();
                                        }}>
                                            Yes
                                        </Button>
                                        <Button variant="secondary" onClick={() => {
                                            setShowConfirmModal(false);
                                        }}>
                                            No
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label className="required">Report Date</Form.Label>
                            <DatePicker name="reportdate" selected={reportdate} autoComplete="off" onChange={(selectedDate) => {
                                setShowResults(false);
                                setReportDate(selectedDate);
                            }}
                                dateFormat="MMM yyyy" showMonthYearPicker className='form-control form-control-sm' placeholderText="Select Report Date"
                                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 2))} />
                            {reportDateError && <div className="invalid-feedback">Report date is required</div>}
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label className="required">Report Format</Form.Label>
                            <Form.Select name="reportformat"
                                onChange={handleFieldChange}
                                value={reportFormat}
                                disabled={localStorage.getItem("medsheetFormat") !== "0"}
                                size="sm">
                                <option value='0'>Select your Format</option>
                                <option value='1'>1</option>
                                <option value='2'>2</option>
                                <option value='3'>3</option>
                                <option value='4'>4</option>
                                <option value='7'>7</option>
                                <option value='8'>8</option>
                                <option value='9'>9</option>
                                <option value='10'>10</option>
                                <option value='11'>11</option>
                                <option value='12'>12</option>
                            </Form.Select>
                            {reportFormatError && <div className="invalid-feedback">Report format is required</div>}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Sort By</Form.Label>
                            <div className="d-flex gap-3"> {/* Horizontal alignment with spacing */}
                                <Form.Check
                                    type="radio"
                                    name="is_drug_hcp"
                                    id="is_drug"
                                    value="drug"
                                    onChange={handleRadioChange}
                                    label="Drug"
                                    defaultChecked={checked}
                                />
                                <Form.Check
                                    type="radio"
                                    name="is_drug_hcp"
                                    id="is_hcp"
                                    value="hcp"
                                    onChange={handleRadioChange}
                                    label="HCP"
                                />
                            </div>
                        </Form.Group>
                    </Col>

                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>MedSheet For</Form.Label>
                            <div className="d-flex gap-3"> {/* Horizontal alignment with spacing */}
                                <Form.Check
                                    type="switch"
                                    name="is_regular"
                                    id="is_regular"
                                    checked={formData.is_regular}
                                    onChange={e => setFormData({ ...formData, is_regular: e.target.checked })}
                                    label="Regular"
                                />
                                <Form.Check
                                    type="switch"
                                    name="is_prn"
                                    id="is_prn"
                                    checked={formData.is_prn}
                                    onChange={e => setFormData({ ...formData, is_prn: e.target.checked })}
                                    label="PRN"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Filter</Form.Label>
                            <Row>
                                <Col>
                                    <Form.Check
                                        type='radio'
                                        name="select_all"
                                        id='select_all'
                                        label='All prescriptions'
                                        checked={formData.select_all}
                                        onChange={e => {
                                            const checked = e.target.checked;
                                            setFormData({
                                                ...formData,
                                                select_all: checked,
                                                selected_drug_only: !checked,
                                                selected_medgroup_only: false
                                            });
                                        }}
                                    />
                                </Col>
                                <Col>
                                    <Form.Check
                                        type='radio'
                                        name="selection_filter"
                                        id='selected_drug_only'
                                        value="selected_drug_only"
                                        label='Selected drug only'
                                        checked={formData.selected_drug_only && !formData.select_all}
                                        // disabled={formData.select_all}
                                        onChange={() =>
                                            setFormData({
                                                ...formData,
                                                selected_drug_only: true,
                                                selected_medgroup_only: false,
                                                select_all: false
                                            })
                                        }
                                    />
                                </Col>
                                <Col>
                                    <Form.Check
                                        type='radio'
                                        name="selection_filter"
                                        id='selected_medgroup_only'
                                        value="selected_medgroup_only"
                                        label='Selected medgroup only'
                                        checked={formData.selected_medgroup_only && !formData.select_all}
                                        // disabled={formData.select_all}
                                        onChange={() =>
                                            setFormData({
                                                ...formData,
                                                selected_drug_only: false,
                                                selected_medgroup_only: true,
                                                select_all: false
                                            })
                                        }
                                    />
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>
                </Row>
                {!formData.select_all && (
                    formData.selected_medgroup_only ? (
                        <AdvanceTableProvider {...table2}>
                            <Row>
                                <Col xs="4" className="mb-2 view-patient-search-holder">
                                    <AdvanceTableSearchBox placeholder="Search..." />
                                </Col>
                                <Col xs="12" className="mb-1 mt-1">
                                    <AdvanceTable
                                        headerClassName="bg-200 text-nowrap align-middle text-center"
                                        rowClassName={row =>
                                            `text-nowrap align-middle text-center${row.original.isExpired ? ' table-danger text-danger' : ''}`
                                        }
                                        tableProps={{
                                            size: 'sm',
                                            striped: true,
                                            className: 'fs-10 mb-0 overflow-hidden'
                                        }}
                                    />
                                </Col>
                                <div className="mt-3">
                                    <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                                </div>
                            </Row>
                        </AdvanceTableProvider>
                    ) : (
                        <AdvanceTableProvider {...table}>
                            <Row className="align-items-end">
                                <Col xs="4" className="mb-2 view-patient-search-holder">
                                    <AdvanceTableSearchBox placeholder="Search..." />
                                </Col>
                                <Col className="mb-2 ms-auto">
                                    <DayProgramToggle
                                        allSelected={allDayProgramSelected}
                                        onToggle={toggleDayProgramMeds}
                                        dayProgramCount={dayProgramIds.length}
                                    />
                                </Col>
                                <Col xs="12" className="mb-1 mt-1">
                                    <AdvanceTable
                                        headerClassName="bg-200 text-nowrap align-middle text-center"
                                        rowClassName="text-nowrap align-middle text-center"
                                        tableProps={{
                                            size: 'sm',
                                            striped: true,
                                            className: 'fs-10 mb-0 overflow-hidden'
                                        }}
                                    />
                                </Col>
                                <div className="mt-3">
                                    <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                                </div>
                            </Row>
                        </AdvanceTableProvider>
                    )
                )}
                <Row>
                    <Col></Col>
                    <Col>
                        <Form.Group>
                            {spinner && (
                                <Button variant="primary" disabled className="mt-3 w-100">
                                    <Spinner
                                        as="span"
                                        animation="grow"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <span className="p-2" style={{ "verticalAlign": "3px" }}>Fetching...</span>
                                </Button>
                            )}
                            {!spinner && (
                                <Button
                                    type="submit"
                                    color="primary"
                                    className="mt-3 w-100">
                                    Generate
                                </Button>
                            )}
                            <div style={{
                                height: 0,
                                overflow: 'hidden',
                                // width: 0
                            }}>
                                {iframeSrc && (
                                    <iframe
                                        src={iframeSrc}
                                        style={{ width: "2000px", height: "500px", border: "none" }}
                                        title="Preview Content"
                                    />
                                )}
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Label><span className="text-danger">*</span> indicates required field</Form.Label>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

MedSheetForm.propTypes = {
    layout: PropTypes.string,
    hasLabel: PropTypes.bool
};

MedSheetForm.defaultProps = {
    layout: 'simple',
    hasLabel: false
};

export default MedSheetForm;

